import { useContext, useState } from 'react';
import { StyleSheet } from 'react-native';
import DotsIcon from '@shared/assets/icons/v2/dots.svg';
import { ActionSheet } from '@shared/components/ActionSheet';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconButton } from '@shared/components/button';
import { MBHover } from '@shared/components/hover';
import { ShareSheet } from '@shared/scenes/cards/details/containers/ShareSheet';
import { useOpenCardOptionsMenu } from '@shared/scenes/cards/details/useOpenCardOptionsMenu';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';

export interface MBCardMenuProps {
  card: API.CardFragment;
  menuIsVisible: boolean;
  onMenuClose(): void;
}

export function MBCardMenu({ card, menuIsVisible, onMenuClose }: MBCardMenuProps) {
  const anchorName = `--card-menu-${card.id}`;
  const colors = useContext(ColorThemeContext);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [showShareSheet, setShowShareSheet] = useState(false);

  const { isMenuOpen, openMenu, closeMenu, menuOptions, removeMenuState, bulkEditMenuState } =
    useOpenCardOptionsMenu(card, {
      onOpen: undefined,
      onClose: onMenuClose,
      onShare: () => setShowShareSheet(true),
    });

  const dropdownOpen = isMenuOpen;

  return (
    <MBView
      style={[
        styles.cardMenuWrapper,
        { borderRadius: grid(1), position: 'absolute', top: 0, right: 0 },
      ]}
    >
      <View style={{ anchorName }}>
        <MBHover onHoverStart={() => setIsHovered(true)} onHoverEnd={() => setIsHovered(false)}>
          <>
            {(menuIsVisible || dropdownOpen) && (
              <MBIconButton
                source={DotsIcon}
                size="small"
                inverted
                onPress={openMenu}
                backgroundColor={isHovered ? colors.highlightActive : colors.highlightInactive}
              />
            )}
          </>
        </MBHover>
      </View>
      {isMenuOpen && (
        <ActionSheet
          anchorName={anchorName}
          align="right"
          onClose={closeMenu}
          options={menuOptions}
        />
      )}
      {removeMenuState.isOpen && (
        <ActionSheet
          anchorName={anchorName}
          align="right"
          title={removeMenuState.removeMenuTitle}
          onClose={removeMenuState.closeRemoveMenu}
          options={removeMenuState.removeMenuOptions}
        />
      )}
      {bulkEditMenuState.isOpen && (
        <ActionSheet
          anchorName={anchorName}
          align="right"
          title={bulkEditMenuState.bulkEditMenuTitle}
          onClose={bulkEditMenuState.closeBulkEditMenu}
          options={bulkEditMenuState.bulkEditMenuOptions}
          style={{ maxWidth: grid(32) }}
        />
      )}
      {showShareSheet && (
        <ShareSheet
          anchorName={anchorName}
          card={{ id: card.id, workflowTemplateId: card.workflowTemplateId ?? null }}
          onCloseSheet={() => setShowShareSheet(false)}
        />
      )}
    </MBView>
  );
}

const styles = StyleSheet.create({
  cardMenuWrapper: {
    padding: grid(1),
    paddingLeft: 0,
  },
});
